// src/pages/projects/components/AddTransactionDialog.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TRANSACTION_TYPES, TRANSACTION_CATEGORIES } from '../../../constants/budgetConstants';

/**
 * Component for the Add Transaction dialog
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Object} props.transaction - Transaction object
 * @param {Function} props.setTransaction - Function to update the transaction
 * @param {Function} props.onAddTransaction - Function to add the transaction
 * @returns {JSX.Element} Add transaction dialog component
 */
const AddTransactionDialog = ({ 
  open, 
  onClose, 
  transaction, 
  setTransaction, 
  onAddTransaction 
}) => {
  // Handle transaction type change
  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setTransaction({
      ...transaction,
      type: newType,
      category: TRANSACTION_CATEGORIES[newType][0].value
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Transaction</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel id="transaction-type-label">Transaction Type</InputLabel>
            <Select
              labelId="transaction-type-label"
              id="transaction-type"
              value={transaction.type}
              onChange={handleTypeChange}
              label="Transaction Type"
            >
              {TRANSACTION_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {type.icon}
                    <Typography sx={{ ml: 1 }}>{type.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Amount"
            value={transaction.amount}
            onChange={(e) => setTransaction({ ...transaction, amount: e.target.value })}
            type="number"
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
            }}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Description"
            value={transaction.description}
            onChange={(e) => setTransaction({ ...transaction, description: e.target.value })}
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="transaction-category-label">Category</InputLabel>
            <Select
              labelId="transaction-category-label"
              id="transaction-category"
              value={transaction.category}
              onChange={(e) => setTransaction({ ...transaction, category: e.target.value })}
              label="Category"
            >
              {TRANSACTION_CATEGORIES[transaction.type].map(cat => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box sx={{ my: 2 }}>
            <DatePicker
              label="Transaction Date"
              value={transaction.date}
              onChange={(date) => setTransaction({ ...transaction, date })}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <FormControl>
              <Typography component="label" variant="body2" htmlFor="invoiced-checkbox">
                <input
                  type="checkbox"
                  id="invoiced-checkbox"
                  checked={transaction.isInvoiced}
                  onChange={(e) => setTransaction({ 
                    ...transaction, 
                    isInvoiced: e.target.checked 
                  })}
                  style={{ marginRight: 8 }}
                />
                Is this transaction invoiced?
              </Typography>
            </FormControl>
          </Box>
          
          {transaction.isInvoiced && (
            <TextField
              fullWidth
              label="Invoice Number"
              value={transaction.invoiceNumber}
              onChange={(e) => setTransaction({ ...transaction, invoiceNumber: e.target.value })}
              margin="normal"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onAddTransaction} 
          variant="contained" 
          disabled={!transaction.amount || !transaction.description}
        >
          Add Transaction
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTransactionDialog;